import FullLayout from 'layout/FullLayout';
import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import qs from 'qs';
import { format, isToday } from 'date-fns';
import { IconSelector, IconCrown, IconTicket } from '@tabler/icons-react';

import CardClass from 'components/CardClass';
import CardMembership from 'components/CardMembership';
import ModalBooking from './ComponentsPage/ModalBooking';
import { openAuthModalEvent } from 'context/AuthModalEvent';
import ClassDetailAside from './ComponentsPage/ClassDetailAside';

import { API_URL, get } from 'lib/network/api';

const Pill = ({ children }) => (
  <span className="bg-white inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium text-kin-text">
    <span className="h-1.5 w-1.5 rounded-full bg-primary-main" aria-hidden />
    {children}
  </span>
);

const fetchDetailClass = async ({ queryKey }) => {
  const [, { id, hasUser }] = queryKey;

  const urlLogin = `${API_URL}/api/event/class/${id}/no-login`;
  const urlHaslogin = `${API_URL}/api/event/class/${id}?with_prices=true`;

  const response = hasUser ? await get(urlHaslogin) : await get(urlLogin);
  return response;
};

const fetchDetailSchedule = async ({ queryKey }) => {
  const [, { schedule_id }] = queryKey;
  const endpoint = `${API_URL}/api/event/schedule/${schedule_id}/no-login?data_type=class_schedule`;
  const response = await get(endpoint);
  return response;
};

const DetailClass = () => {
  const navigation = useNavigate();
  const userProfile = useSelector((state) => state.authProfile);
  const user = userProfile?.data ? userProfile?.data : null;
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const currentParams = qs.parse(searchParams.toString(), { comma: true });
  const { schedule_id } = currentParams;
  const [openBooking, setOpenBooking] = useState(false);
  const [showPrice, setShowPrice] = useState(true);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [activeMembershipForBooking, setActiveMembershipForBooking] = useState(null);

  const formatPickDate = (paramsDate) => {
    const labelToday = isToday(paramsDate) ? 'Today, ' : '';
    return `${labelToday}${format(paramsDate, 'EEEE d MMMM')}`;
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['detailClass', { id, hasUser: !!user }],
    queryFn: fetchDetailClass,
    enabled: !!id,
    retry: 1,
  });

  useEffect(() => {
    if (data?.data?.packages?.length > 0 && !selectedPrice) {
      setSelectedPrice(data.data.packages[0].id);
    }
    if (data?.data?.membership?.length > 0 && !selectedMembership) {
      setSelectedMembership(data.data.membership[0].id);
    }
  }, [data]);

  const {
    data: scheduleDetailData,
    isLoading: loadingScheduleDetailData,
    error: errorScheduleDetailData,
  } = useQuery({
    queryKey: ['detailSchedule', { schedule_id }],
    queryFn: fetchDetailSchedule,
    enabled: !!schedule_id,
    retry: 1,
  });

  const onBooking = (membershipItem) => {
    setOpenBooking(true);
    setActiveMembershipForBooking(membershipItem);
  };

  const goToCheckout = (classId, scheduleId, passId) => {
    if (user) {
      navigation(
        `/checkout?package_type=class&class_id=${classId}&schedule_id=${scheduleId}&package_id=${passId}`,
      );
    } else {
      const url = `/checkout?package_type=class&class_id=${classId}&schedule_id=${scheduleId}&package_id=${passId}`;
      sessionStorage.setItem('kin_pending_redirect', url);
      openAuthModalEvent('unauthenticated');
    }
  };

  // Loading State
  if (isLoading || loadingScheduleDetailData) {
    return (
      <FullLayout>
        <div className="container mx-auto pt-6 pb-24 px-4 xl:px-0">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-main mx-auto"></div>
              <p className="mt-6 text-kin-text font-semibold text-lg">Loading class details...</p>
              <p className="mt-2 text-gray-500 text-sm">Please wait a moment</p>
            </div>
          </div>
        </div>
      </FullLayout>
    );
  }

  // Error State
  if (error || errorScheduleDetailData) {
    return (
      <FullLayout>
        <div className="container mx-auto pt-6 pb-24 px-4 xl:px-0">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center max-w-md">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-kin-text mb-2">Failed to Load Class</h2>
              <p className="text-gray-600 mb-6">
                {error?.message ||
                  errorScheduleDetailData?.message ||
                  'We encountered an issue while loading the class details. Please try again.'}
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => navigation('/learn/our-class')}
                  className="px-6 py-2.5 bg-gray-200 text-kin-text rounded-full font-semibold hover:bg-gray-300 transition-colors">
                  Back to Classes
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-2.5 bg-primary-main text-white rounded-full font-semibold hover:bg-primary-dark transition-colors">
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </FullLayout>
    );
  }

  // No Data State
  if (!data?.data?.class) {
    return (
      <FullLayout>
        <div className="container mx-auto pt-6 pb-24 px-4 xl:px-0">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center max-w-md">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-kin-text mb-2">Class Not Found</h2>
              <p className="text-gray-600 mb-6">
                The class you're looking for doesn't exist or has been removed.
              </p>
              <button
                onClick={() => navigation('/learn/our-class')}
                className="px-6 py-2.5 bg-primary-main text-white rounded-full font-semibold hover:bg-primary-dark transition-colors">
                Browse All Classes
              </button>
            </div>
          </div>
        </div>
      </FullLayout>
    );
  }

  return (
    <FullLayout>
      <div className="container mx-auto pt-4 pb-24 px-2 sm:px-4 xl:px-0">
        <div className="flex flex-col-reverse gap-8 lg:grid lg:grid-cols-12 items-start text-kin-text">
          <article className="flex flex-col gap-y-4 col-span-8 w-full">
            <section className="p-4 sm:p-6 bg-white rounded-2xl shadow-md">
              <div className="flex flex-col gap-4">
                <div>
                  <span className="mt-4 text-sm text-primary-main font-normal uppercase">
                    {data?.data?.class?.subject || '-'}
                  </span>
                  <h1 className="text-xl font-bold leading-none max-h-[55px]">
                    {data?.data?.class?.name || '-'}
                  </h1>
                  <span className="text-base font-semibold">
                    Theme ({data?.data?.class?.theme || '-'})
                  </span>

                  <div
                    className="text-sm font-medium mt-4"
                    dangerouslySetInnerHTML={{ __html: data?.data?.class?.description || '-' }}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 w-full">
                  <div>
                    <h3 className="text-base font-semibold text-kin-text">What You’ll Discover</h3>
                    <p className="mt-2 text-sm text-kin-text/80 leading-relaxed">
                      {data?.data?.class?.what_to_expect || data?.data?.class?.description || '-'}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-kin-text mt-4 md:mt-0">
                      What to Bring
                    </h3>
                    <ul className="mt-2 flex flex-wrap gap-2">
                      <Pill>
                        {data?.data?.class?.requirements ||
                          'No special items needed — just bring your curiosity!'}
                      </Pill>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <section className="p-4 sm:p-6 bg-white rounded-2xl shadow-md">
              <h2 className="font-bold text-lg text-kin-text">Class Passes Options</h2>
              <p className="text-sm text-kin-text/70">
                Choose your preferred Class Pass — use one you already have or get a new one to join
                the fun!
              </p>

              <div className="flex flex-col gap-10 mt-6">
                {user ? (
                  // User is logged in - show membership passes
                  <>
                    {data?.data?.membership?.length > 0 && (
                      <div className="flex flex-col gap-y-2">
                        <div className="flex flex-row items-center justify-between">
                          <h2 className="text-base font-semibold text-kin-text inline-flex gap-2 items-center justify-center whitespace-nowrap">
                            <IconCrown className="w-6 h-6" />
                            Use Your Class Pass
                          </h2>

                          <Link
                            to="/my-passes"
                            className="text-primary-main text-sm font-medium hover:underline">
                            Check Your Active Passes
                          </Link>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-stretch">
                          {data.data.membership.map((membership) => {
                            const relatedPackage = data.data.membership_packages?.find(
                              (pkg) => pkg.id === membership.package_id,
                            );
                            if (!relatedPackage) return null;

                            const isSelected = selectedMembership === membership.id;

                            return (
                              <div key={membership.id}>
                                <CardMembership
                                  isSelected={isSelected}
                                  onSelected={() => setSelectedMembership(membership.id)}
                                  onBookMembership={onBooking}
                                  packageItem={relatedPackage}
                                  membership={membership}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  // User is not logged in - show login prompt for membership
                  <div className="w-full rounded-2xl border-2 border-primary-main/20 bg-gradient-to-br from-primary-main/5 to-primary-main/10 p-8 text-center">
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center">
                        <IconCrown className="w-8 h-8 text-primary-main" />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-kin-text mb-2">View Your Class Passes</h3>
                    <p className="text-sm text-kin-text/70 mb-6 max-w-md mx-auto">
                      Sign in to see your active class passes and book this class using your
                      existing membership. If you don't have a pass yet, you can purchase one below.
                    </p>
                    <button
                      onClick={() => openAuthModalEvent('unauthenticated')}
                      className="inline-flex items-center justify-center px-6 py-2.5 bg-primary-main text-white rounded-full font-semibold hover:bg-primary-dark transition-colors">
                      Sign In to View Passes
                    </button>
                  </div>
                )}

                {data?.data?.packages?.length > 0 && (
                  <div className="flex flex-col gap-y-4">
                    <div className="flex flex-row justify-between items-end">
                      <div>
                        <h2 className="font-bold text-lg text-kin-text inline-flex gap-2 items-center justify-center whitespace-nowrap">
                          <IconTicket className="w-6 h-6" />
                          Get a New Class Pass
                        </h2>
                        <p className="text-sm text-kin-text/70">
                          Don’t have a pass yet? Purchase one below to join this exciting class!
                        </p>
                      </div>
                      <button onClick={() => setShowPrice(!showPrice)} className="py-2">
                        <IconSelector className="w-6 h-6 text-kin-text" />
                      </button>
                    </div>

                    {showPrice && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-stretch">
                        {data.data.packages.map((item) => (
                          <div key={item.id}>
                            <CardClass
                              item={item}
                              context="detail"
                              isSelected={selectedPrice === item.id}
                              onSelected={() => setSelectedPrice(item.id)}
                              onCheckout={(val) => goToCheckout(id, schedule_id, val)}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {!data?.data?.membership?.length && !data?.data?.packages?.length && user && (
                  <div className="w-full rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                    <p className="text-base font-semibold text-gray-700">
                      No Class Pass Options Available
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      This class doesn’t currently offer passes or memberships.
                    </p>
                    <p className="text-sm text-gray-500">
                      Please check back later for new options!
                    </p>
                  </div>
                )}
              </div>
            </section>
          </article>

          <ClassDetailAside
            data={data}
            scheduleDetailData={scheduleDetailData}
            selectedMembership={selectedMembership}
            selectedPrice={selectedPrice}
            setOpenBooking={setOpenBooking}
            goToCheckout={goToCheckout}
            id={id}
            schedule_id={schedule_id}
            formatPickDate={formatPickDate}
          />

          {openBooking && (
            <ModalBooking
              schedule={scheduleDetailData}
              selectedMembership={activeMembershipForBooking}
              openModal={openBooking}
              setOpenModal={setOpenBooking}
              onAuthRequired={() => openAuthModalEvent('expired')}
            />
          )}
        </div>
      </div>
    </FullLayout>
  );
};

export default DetailClass;
